import type {
  CreateSavedViewInput,
  SavedViewsInput,
  UpdateSavedViewAudienceInput,
  UpdateSavedViewInput,
  useApi,
} from '@repo/api'
import type {
  DefaultTableConfiguration,
  SavedViewDetail,
  SavedViewSummary,
  TableConfigurationJson,
} from '@repo/schema/table-configuration'
import type { QueryClient } from '@repo/tanstack/query'
import { isResponseError } from '@repo/upfetch'

import {
  normalizeBackendTableSettings,
  type StoredTableSettings,
} from '../table/context/merge-table-settings'
import type { TableColumnOwner, TableColumnSource } from '../table/context/table-column-owner'
import { DEFAULT_ROW_DENSITY, type RowDensity } from '../table/row-density'
import { type TableConfigurationDefinition, viewPath } from './definition'

export type SavedViewMetadataInput = Omit<UpdateSavedViewInput, 'configuration' | 'schema_version'>

/** What a new view starts from: the table as shown, or only its locked columns and default query. */
export type SavedViewStart = 'blank' | 'current'

export type TableConfigurationBaseline<TQuery> = {
  columns: StoredTableSettings | undefined
  configurationUpdatedAt: string
  query: TQuery
}

export type TableConfigurationConnection = {
  api: TableConfigurationsApi
  navigate: { push: (_href: string) => void; replace: (_href: string) => void }
  queryClient: QueryClient
}

export type TableConfigurationLoadStatus = 'error' | 'idle' | 'loading' | 'ready'

export type TableConfigurationNotice = {
  id: number
  kind: 'view-unavailable' | 'widths-not-saved' | 'wrong-organisation'
}

export type TableConfigurationSnapshot<TQuery> = {
  actions: {
    discardChanges: boolean
    manage: boolean
    pin: boolean
    reload: boolean
    saveAsNew: boolean
    /** A Blank start needs the table's columns, so it waits until the table has reported them. */
    saveBlank: boolean
    saveChanges: boolean
    /** Columns only, never the query; from Default or from inside a view. */
    saveColumnsToDefault: boolean
  }
  /** False outside the registered pathname or without the table permission. */
  active: boolean
  /** Default compares columns only; its query is transient. A view compares query and columns. */
  changed: boolean
  default: {
    error: Error | null
    resource: DefaultTableConfiguration | null
    status: TableConfigurationLoadStatus
  }
  /** The row density shown; it is saved with the columns. */
  density: RowDensity
  notices: readonly TableConfigurationNotice[]
  /** A Saved View being opened from the picker; the current selection stays until it loads. */
  opening: null | number
  pinned: {
    error: Error | null
    items: readonly SavedViewSummary[]
    status: TableConfigurationLoadStatus
  }
  /** A resize's widths are saving automatically; while they save they don't count as Changed. */
  savingWidths: boolean
  /** A newer canonical configuration of the active view. Never applied automatically. */
  updated: null | SavedViewDetail
  /** `null` is Default. */
  view: null | {
    baseline: null | TableConfigurationBaseline<TQuery>
    error: Error | null
    id: number
    status: TableConfigurationLoadStatus
    summary: null | SavedViewSummary
  }
}

/** An autosave, for Default (`viewId: null`) or a view, in the Organisation it was made in. */
type Autosave = {
  autosaved: Autosaved
  /** The view's saved version, and the configuration to send, as they were when queued. */
  baselineAt: null | string
  configuration: SavedConfiguration
  connection: TableConfigurationConnection
  epoch: number
  viewId: null | number
}

/** What saves automatically where the user may save: column widths and row density. */
type Autosaved = { density: RowDensity | undefined; sizing: Sizing }

type SavedConfiguration = { columns: TableConfigurationJson; query: TableConfigurationJson }

type Sizing = NonNullable<StoredTableSettings['sizing']>

type TableConfigurationsApi = ReturnType<typeof useApi>['tableConfigurations']

const AUTOSAVE_DELAY = 500

export class UnsupportedTableConfigurationError extends Error {
  constructor(schemaVersion: number) {
    super(`Table Configuration schema version ${schemaVersion} is not supported`)
    this.name = 'UnsupportedTableConfigurationError'
  }
}

const PINNED_INPUT: SavedViewsInput = { access: 'pinned', perPage: 100 }

type Settled<TValue> = { error: Error; ok: false } | { ok: true; value: TValue }

/** Settle a request at the I/O boundary so callers branch on a typed failure. */
const settle = <TValue>(request: Promise<TValue>): Promise<Settled<TValue>> =>
  request.then(
    value => ({ ok: true, value }),
    error => ({ error: error instanceof Error ? error : new Error(String(error)), ok: false }),
  )

/** The listed pins fill the slots they hold, in the listed order; the others keep theirs. */
const reorderListed = (items: readonly SavedViewSummary[], ids: readonly number[]) => {
  const listed = new Set(ids)
  const inOrder = ids.flatMap(id => items.find(item => item.id === id) ?? []).values()
  return items.map(item => (listed.has(item.id) ? (inOrder.next().value ?? item) : item))
}

const isAccessLost = (error: Error) =>
  isResponseError(error) && (error.status === 403 || error.status === 404)

/** The view a table path names: `null` on the list itself, `'invalid'` for a malformed id. */
const readViewId = (pathname: string, listPath: string): 'invalid' | null | number => {
  if (pathname === listPath) return null
  const raw = pathname.slice(`${listPath}/view/`.length)
  return /^\d+$/.test(raw) ? Number(raw) : 'invalid'
}

const sortedJson = <TValue>(record: Record<string, TValue> | undefined) =>
  JSON.stringify(Object.entries(record ?? {}).sort(([a], [b]) => a.localeCompare(b)))

/** Display choices in one order of columns and of each column's choices. */
const displayJson = (display: StoredTableSettings['display']) =>
  sortedJson(
    Object.fromEntries(
      Object.entries(display ?? {}).map(([id, choices]) => [id, sortedJson(choices)]),
    ),
  )

const autosavedOf = ({ density, sizing }: StoredTableSettings): Autosaved => ({
  density,
  sizing: sizing ?? {},
})

const equalAutosaved = (a: Pick<StoredTableSettings, 'density' | 'sizing'>, b: typeof a) =>
  sortedJson(a.sizing) === sortedJson(b.sizing) &&
  (a.density ?? DEFAULT_ROW_DENSITY) === (b.density ?? DEFAULT_ROW_DENSITY)

export const equalTableColumns = (a: StoredTableSettings, b: StoredTableSettings) =>
  JSON.stringify(a.order ?? []) === JSON.stringify(b.order ?? []) &&
  JSON.stringify([a.pinning?.left ?? [], a.pinning?.right ?? []]) ===
    JSON.stringify([b.pinning?.left ?? [], b.pinning?.right ?? []]) &&
  equalAutosaved(a, b) &&
  sortedJson(a.visibility) === sortedJson(b.visibility) &&
  // Both sides are what a table showed, so a source without stored lists compares as merged.
  sortedJson(a.fields) === sortedJson(b.fields) &&
  sortedJson(a.itemsShown) === sortedJson(b.itemsShown) &&
  displayJson(a.display) === displayJson(b.display) &&
  // Each side carries its source's unknown keys, so these differ only when the sources do.
  JSON.stringify(a.unknownKeys ?? {}) === JSON.stringify(b.unknownKeys ?? {})

const toColumnsJson = (columns: StoredTableSettings): TableConfigurationJson => {
  // Keys this build does not know go back as stored.
  const json: TableConfigurationJson = { ...columns.unknownKeys }
  // Fields the columns never stored stay unstored, so code defaults still apply to them.
  if (columns.pinning) {
    json.pinning = { left: columns.pinning.left ?? [], right: columns.pinning.right ?? [] }
  }
  if (columns.order) json.order = columns.order
  if (columns.orderVersion !== undefined) json.orderVersion = columns.orderVersion
  if (columns.sizing) json.sizing = columns.sizing
  if (columns.visibility) json.visibility = columns.visibility
  if (columns.fields) json.fields = columns.fields
  if (columns.itemsShown) json.itemsShown = columns.itemsShown
  if (columns.display) json.display = columns.display
  if (columns.density) json.density = columns.density
  return json
}

/**
 * One controller per provider mount. It owns Default, the active Saved View, summaries, pins,
 * the `view` URL value, Changed and Updated, and the table's column persistence. Requests go
 * through the TanStack Query cache; the controller keeps only the opened baseline and flags.
 */
export const createTableConfigurationController = <TQuery extends TableConfigurationJson>(
  definition: TableConfigurationDefinition<TQuery>,
) => {
  type Snapshot = TableConfigurationSnapshot<TQuery>
  type ViewState = NonNullable<Snapshot['view']>

  const path = { tableKey: definition.tableKey }
  const savedViewsKey = ['table-configurations', definition.tableKey, 'saved-views']

  let connection: null | TableConfigurationConnection = null
  let allowed = false
  let contextKey = ''
  let location: null | { pathname: string; search: string } = null
  let wasActive = false
  let loadedContext: null | string = null
  let viewGeneration = 0
  let noticeId = 0

  let state: Omit<Snapshot, 'actions' | 'active' | 'changed' | 'density' | 'savingWidths'> = {
    default: { error: null, resource: null, status: 'idle' },
    notices: [],
    opening: null,
    pinned: { error: null, items: [], status: 'idle' },
    updated: null,
    view: null,
  }

  let columnSource: TableColumnSource = { columns: undefined, revision: 0 }
  let appliedColumns: null | StoredTableSettings = null
  let liveColumns: null | StoredTableSettings = null
  /** The table's Blank start, as it last reported it; `null` while no table is mounted. */
  let blankColumns: null | StoredTableSettings = null
  /** The density shown. The table reports columns without it, so the controller keeps it. */
  let density: RowDensity | undefined

  let pendingAutosave: Autosave | null = null
  let autosaveTimer: ReturnType<typeof setTimeout> | undefined
  /** What was last autosaved for the shown columns; `null` until an autosave is sent. */
  let sentAutosaved: Autosaved | null = null
  let autosavesInFlight = 0
  /** Bumped by Discard, Reload and manual saves, so an autosave they superseded changes nothing. */
  let autosaveEpoch = 0
  /** Saves of columns run one at a time, so each lands after the one before. */
  let writes: Promise<void> = Promise.resolve()
  /** Pin reorders save one at a time, in the order the user made them. */
  let pinOrderWrites: Promise<unknown> = Promise.resolve()
  /** Bumped by every reorder, so a pins load started before it never shows the older order. */
  let pinOrderVersion = 0
  /** Reorders not yet settled. Pins load only once none is, so a load never returns an older order. */
  let pinOrderSaves = 0
  let reloadPinsAfterSaves = false
  const listeners = new Set<() => void>()
  const columnListeners = new Set<() => void>()

  const viewPrefix = viewPath(definition, '')
  const isTablePath = (pathname: string) =>
    pathname === definition.pathname || pathname.startsWith(viewPrefix)

  const isActive = () =>
    allowed && connection !== null && location !== null && isTablePath(location.pathname)

  const currentSearch = () => new URLSearchParams(location?.search ?? '')

  const liveQuery = () => definition.query.read(currentSearch())

  /** The widths and density an autosave is saving, waiting or sent. */
  const savingAutosaved = () =>
    pendingAutosave?.autosaved ?? (autosavesInFlight > 0 ? sentAutosaved : null)

  const columnsChanged = () => {
    if (appliedColumns === null || liveColumns === null) return false
    const saving = savingAutosaved()
    // Widths and density on their way to being saved aren't for the user to save or discard.
    const savingSizing =
      saving && sortedJson(liveColumns.sizing ?? {}) === sortedJson(saving.sizing)
    const savingDensity =
      saving &&
      (liveColumns.density ?? DEFAULT_ROW_DENSITY) === (saving.density ?? DEFAULT_ROW_DENSITY)
    const live = {
      ...liveColumns,
      ...(savingSizing && { sizing: appliedColumns.sizing }),
      ...(savingDensity && { density: appliedColumns.density }),
    }
    return !equalTableColumns(live, appliedColumns)
  }

  const isChanged = () => {
    const { view } = state
    if (view === null) return columnsChanged()
    if (!view.baseline) return false
    return !definition.query.equals(liveQuery(), view.baseline.query) || columnsChanged()
  }

  const buildSnapshot = (): Snapshot => {
    const active = isActive()
    const changed = active && isChanged()
    const { updated, view } = state
    const ready = active && view?.status === 'ready'
    const isOwner = ready && view.summary?.is_owner === true

    return Object.freeze({
      ...state,
      actions: {
        discardChanges: changed && (view === null || ready),
        manage: isOwner,
        pin: ready,
        reload: ready && updated !== null,
        // Only something new is worth saving: a view's changes, or Default's filters or columns.
        saveAsNew:
          view === null
            ? changed || (active && !definition.query.equals(liveQuery(), definition.defaultQuery))
            : ready && changed,
        saveBlank: active && blankColumns !== null,
        saveChanges: isOwner && changed,
        saveColumnsToDefault: liveColumns !== null && (view === null ? changed : ready),
      },
      active,
      changed,
      density: density ?? DEFAULT_ROW_DENSITY,
      // Only the shown view's autosave: a save still landing for a view left behind isn't shown.
      savingWidths: savingAutosaved() !== null,
    })
  }

  let snapshot = buildSnapshot()
  const serverSnapshot = snapshot

  const emit = () => {
    snapshot = buildSnapshot()
    listeners.forEach(listener => listener())
  }

  const setColumnSource = (columns: StoredTableSettings | undefined) => {
    // A resize saves to the columns it was made on, even when the table moves on before the delay.
    sendAutosave()
    sentAutosaved = null
    columnSource = { columns, revision: columnSource.revision + 1 }
    appliedColumns = null
    density = columns?.density
    columnListeners.forEach(listener => listener())
  }

  /**
   * Makes saved columns the baseline, and the source the table applies again when it re-runs,
   * without a new revision, so what the table shows stays.
   */
  const adoptColumns = (columns: null | StoredTableSettings) => {
    appliedColumns = columns
    if (columns) columnSource = { ...columnSource, columns }
  }

  /**
   * A Default saved for the first time keeps every column and Column field it shows at code
   * defaults, so a column added after rollout stays shown; edits not yet saved stay out.
   */
  const firstDefaultColumns = (): StoredTableSettings => ({
    fields: appliedColumns?.fields,
    visibility: appliedColumns?.visibility,
  })

  /** Makes autosaved values part of the baseline and the source, so they no longer count as Changed. */
  const adoptAutosaved = (autosaved: Autosaved) => {
    // Values saved now settle any earlier failure to save them.
    state = {
      ...state,
      notices: state.notices.filter(notice => notice.kind !== 'widths-not-saved'),
    }
    // A Default saved for the first time now stores what `withAutosaved` sent with them.
    const source = columnSource.columns ?? firstDefaultColumns()
    if (appliedColumns) appliedColumns = { ...appliedColumns, ...autosaved }
    columnSource = { ...columnSource, columns: { ...source, ...autosaved } }
  }

  const dropPendingAutosave = () => {
    clearTimeout(autosaveTimer)
    pendingAutosave = null
  }

  const cancelAutosave = () => {
    dropPendingAutosave()
    sentAutosaved = null
    autosaveEpoch++
  }

  const serializeWrite = <TValue>(write: () => Promise<TValue>) => {
    const run = writes.then(write)
    writes = run.then(
      () => {},
      () => {},
    )
    return run
  }

  /** Whether the table still shows the columns an autosave was made on. */
  const isShown = ({ connection: saved, viewId }: Autosave) =>
    saved === connection && (viewId === null ? state.view === null : state.view?.id === viewId)

  /** The saved configuration, as last saved, with only its autosaved values replaced. */
  const withAutosaved = (
    viewId: null | number,
    autosaved: Autosaved,
  ): null | SavedConfiguration => {
    if (viewId === null) {
      const { resource } = state.default
      const saved = resource
        ? normalizeBackendTableSettings(resource.configuration.columns)
        : firstDefaultColumns()
      return { columns: toColumnsJson({ ...saved, ...autosaved }), query: {} }
    }
    const baseline = state.view?.id === viewId ? state.view.baseline : null
    if (!baseline) return null
    return {
      columns: toColumnsJson({ ...baseline.columns, ...autosaved }),
      query: encodeQuery(baseline.query),
    }
  }

  const isNewer = (detail: SavedViewDetail, than: null | string) =>
    than !== null && Date.parse(detail.configuration_updated_at) > Date.parse(than)

  /**
   * A resize or a density change saves automatically where the user may save: Default, or a view
   * they own. Only the widths and density change; other edits stay Changed until saved by hand.
   * A density change keeps the widths last saved, or a resize still waiting to be saved, so widths
   * a reset brought back stay Changed.
   */
  const queueAutosave = (change: 'density' | 'sizing') => {
    const pendingSizing = pendingAutosave?.autosaved.sizing
    dropPendingAutosave()
    if (!isActive() || !connection || !appliedColumns || !liveColumns) return
    const live = autosavedOf(liveColumns)
    const autosaved =
      change === 'sizing'
        ? live
        : {
            ...live,
            sizing: pendingSizing ?? (sentAutosaved ?? autosavedOf(appliedColumns)).sizing,
          }
    // A change back to what is saved, or already sent, has nothing new to save.
    if (equalAutosaved(autosaved, sentAutosaved ?? appliedColumns)) return
    const { updated, view } = state
    if (view === null && state.default.status !== 'ready') return
    // A newer saved configuration would be overwritten; the change stays Changed instead.
    if (view && (view.status !== 'ready' || !view.summary?.is_owner || updated)) return
    const viewId = view?.id ?? null
    const configuration = withAutosaved(viewId, autosaved)
    if (!configuration) return

    const baselineAt = view?.baseline?.configurationUpdatedAt ?? null
    pendingAutosave = {
      autosaved,
      baselineAt,
      configuration,
      connection,
      epoch: autosaveEpoch,
      viewId,
    }
    autosaveTimer = setTimeout(sendAutosave, AUTOSAVE_DELAY)
  }

  function sendAutosave() {
    clearTimeout(autosaveTimer)
    const save = pendingAutosave
    pendingAutosave = null
    if (!save) return
    if (isShown(save)) sentAutosaved = save.autosaved
    autosavesInFlight++
    serializeWrite(() => saveAutosaved(save))
      .catch(error => {
        console.error('Failed to save column widths and row density:', error)
        if (save.epoch !== autosaveEpoch || !isShown(save)) return
        // A later autosave, still saving, holds every width and the density: its own result decides.
        if (sentAutosaved !== save.autosaved || pendingAutosave) return
        // The change stays Changed, so Save changes still works.
        sentAutosaved = null
        addNotice('widths-not-saved')
      })
      .finally(() => {
        autosavesInFlight--
        emit()
      })
  }

  async function saveAutosaved(save: Autosave) {
    if (save.epoch !== autosaveEpoch) return
    const { api, queryClient } = save.connection
    const { autosaved, viewId } = save
    // Rebuilt on what is saved now, so a manual save that landed first is kept.
    const configuration = (isShown(save) && withAutosaved(viewId, autosaved)) || save.configuration
    const input = { configuration, schema_version: definition.schemaVersion }
    const isCurrent = () => save.epoch === autosaveEpoch && isShown(save)

    if (viewId === null) {
      const resource = await api.updateDefault.mutate({ input, path })
      if (save.connection !== connection) return
      acceptDefault(resource)
      if (isCurrent()) adoptAutosaved(autosaved)
      return emit()
    }

    const viewPath = { ...path, id: viewId }
    const baselineAt =
      (isShown(save) && state.view?.baseline?.configurationUpdatedAt) || save.baselineAt
    // A newer configuration saved elsewhere would be overwritten; the change stays Changed instead.
    const latest = await queryClient.fetchQuery(
      api.savedView.queryOptions({ path: viewPath }, { staleTime: 0 }),
    )
    if (isNewer(latest, baselineAt)) {
      if (isCurrent()) state = { ...state, updated: latest }
      return emit()
    }

    const detail = await api.updateSavedView.mutate({ input, path: viewPath })
    // A view deleted, discarded or left meanwhile keeps what it has.
    if (!isCurrent()) return
    acceptDetail(detail)
    const { updated, view } = state
    if (view?.id === viewId && view.baseline) {
      const baseline = {
        ...view.baseline,
        columns: { ...view.baseline.columns, ...autosaved },
        configurationUpdatedAt: detail.configuration_updated_at,
      }
      const stillNewer = updated && isNewer(updated, detail.configuration_updated_at)
      state = { ...state, updated: stillNewer ? updated : null, view: { ...view, baseline } }
      adoptAutosaved(autosaved)
    }
    emit()
  }

  /** Make a returned Default the loaded one, in the controller and the query cache. */
  const acceptDefault = (resource: DefaultTableConfiguration) => {
    connection?.queryClient.setQueryData(connection.api.default.queryKey({ path }), resource)
    state = { ...state, default: { error: null, resource, status: 'ready' } }
  }

  const applyDefaultColumns = () => {
    const { resource } = state.default
    setColumnSource(
      resource ? normalizeBackendTableSettings(resource.configuration.columns) : undefined,
    )
  }

  const toBaseline = (detail: SavedViewDetail): null | TableConfigurationBaseline<TQuery> => {
    if (detail.schema_version > definition.schemaVersion) return null
    const query = definition.query.decode(detail.configuration.query, detail.schema_version)
    if (query === null) return null

    return {
      columns: normalizeBackendTableSettings(detail.configuration.columns),
      configurationUpdatedAt: detail.configuration_updated_at,
      query,
    }
  }

  const navigate = (
    mode: 'push' | 'replace',
    target: { pathname: string; search: URLSearchParams },
  ) => {
    if (!connection || !location) return
    const query = target.search.toString()
    location = { pathname: target.pathname, search: query }
    connection.navigate[mode](`${target.pathname}${query ? `?${query}` : ''}`)
  }

  /** The list, or a view's own path. */
  const pathFor = (id: null | number) =>
    id === null ? definition.pathname : viewPath(definition, id)

  /** The table URL for `query`: the list, or the view's own path. */
  const urlFor = (query: TQuery, id: null | number) => ({
    pathname: pathFor(id),
    search: definition.query.write(currentSearch(), query),
  })

  const addNotice = (kind: TableConfigurationNotice['kind']) => {
    state = { ...state, notices: [...state.notices, { id: ++noticeId, kind }] }
  }

  const setView = (view: null | ViewState) => {
    state = { ...state, opening: null, updated: null, view }
  }

  const fetchDetail = (id: number, fresh = false) => {
    if (!connection) throw new Error('Table Configuration controller is not connected')
    const { api, queryClient } = connection
    return queryClient.fetchQuery(
      api.savedView.queryOptions({ path: { ...path, id } }, fresh ? { staleTime: 0 } : undefined),
    )
  }

  const returnToDefault = (mode: 'push' | 'replace', notice?: TableConfigurationNotice['kind']) => {
    viewGeneration++
    setView(null)
    navigate(mode, urlFor(definition.defaultQuery, null))
    applyDefaultColumns()
    if (notice) addNotice(notice)
    emit()
  }

  /** Activate a view the URL already names: deep link, reload, or back/forward. */
  const activateFromUrl = async (id: number) => {
    const generation = ++viewGeneration
    setView({ baseline: null, error: null, id, status: 'loading', summary: null })
    setColumnSource(undefined)
    emit()

    const result = await settle(fetchDetail(id))
    if (generation !== viewGeneration) return
    if (!result.ok) {
      if (isAccessLost(result.error)) return returnToDefault('replace', 'view-unavailable')
      setView({ baseline: null, error: result.error, id, status: 'error', summary: null })
      return emit()
    }
    const detail = result.value
    if (!detail.available) return returnToDefault('replace', 'wrong-organisation')

    const baseline = toBaseline(detail)
    if (!baseline) {
      const error = new UnsupportedTableConfigurationError(detail.schema_version)
      setView({ baseline: null, error, id, status: 'error', summary: detail })
      return emit()
    }

    setView({ baseline, error: null, id, status: 'ready', summary: detail })
    // A link that names only the view opens it as saved. A URL carrying query state keeps it,
    // so a Changed view survives reload and history navigation.
    if (definition.query.equals(liveQuery(), definition.defaultQuery)) {
      navigate('replace', urlFor(baseline.query, id))
    }
    setColumnSource(baseline.columns)
    emit()
  }

  const loadDefault = async () => {
    if (!connection) return
    const { api, queryClient } = connection
    state = { ...state, default: { ...state.default, error: null, status: 'loading' } }
    emit()

    const result = await settle(queryClient.fetchQuery(api.default.queryOptions({ path })))
    const resource = result.ok ? result.value : null
    const isUnsupported = resource !== null && resource.schema_version > definition.schemaVersion
    const error = result.ok
      ? isUnsupported && new UnsupportedTableConfigurationError(resource.schema_version)
      : result.error
    state = {
      ...state,
      default: error
        ? { error, resource: null, status: 'error' }
        : { error: null, resource, status: 'ready' },
    }
    if (state.view === null) applyDefaultColumns()
    emit()
  }

  const loadPinned = async (fresh = false): Promise<void> => {
    if (!connection) return
    if (pinOrderSaves > 0) {
      reloadPinsAfterSaves = true
      return
    }
    const { api, queryClient } = connection
    state = { ...state, pinned: { ...state.pinned, error: null, status: 'loading' } }
    emit()

    const version = pinOrderVersion
    const result = await settle(
      queryClient.fetchQuery(
        api.savedViews.queryOptions(
          { input: PINNED_INPUT, path },
          fresh ? { staleTime: 0 } : undefined,
        ),
      ),
    )
    // A reorder started meanwhile, so this response may hold the older order.
    if (version !== pinOrderVersion) return loadPinned(true)
    state = {
      ...state,
      pinned: result.ok
        ? { error: null, items: result.value.data, status: 'ready' }
        : { ...state.pinned, error: result.error, status: 'error' },
    }
    emit()
  }

  /** Pins and Default load once per Organisation context on any route, for the sidebar. */
  const loadOffTable = () => {
    if (!allowed || !connection || loadedContext === contextKey) return
    loadedContext = contextKey
    void loadDefault()
    void loadPinned()
  }

  const reconcile = (force = false) => {
    if (!isActive() || !location) {
      if (wasActive) {
        // Leaving the registered route discards the active view and its local changes.
        wasActive = false
        viewGeneration++
        setView(null)
        setColumnSource(undefined)
      }
      loadOffTable()
      return emit()
    }

    if (!wasActive || force) {
      wasActive = true
      loadedContext = contextKey
      void loadDefault()
      void loadPinned()
    }

    const id = readViewId(location.pathname, definition.pathname)
    if (id === 'invalid') return returnToDefault('replace', 'view-unavailable')
    if (id === null) {
      if (state.view !== null) {
        viewGeneration++
        setView(null)
        applyDefaultColumns()
      }
      return emit()
    }
    if (state.view?.id === id && !force) return emit()
    void activateFromUrl(id)
  }

  /** Replace local projections with the authoritative resource a mutation returned. */
  const acceptDetail = (detail: SavedViewDetail) => {
    connection?.queryClient.setQueryData(
      connection.api.savedView.queryKey({ path: { ...path, id: detail.id } }),
      detail,
    )
    const { view } = state
    if (view?.id === detail.id && view.baseline) {
      state = { ...state, view: { ...view, summary: detail } }
    }
    void connection?.queryClient.invalidateQueries({ queryKey: savedViewsKey })
  }

  /** Open a returned resource as the saved view; `null` when this client can't read it. */
  const openAsSaved = (detail: SavedViewDetail) => {
    const baseline = toBaseline(detail)
    if (!baseline) return null
    // An autosave waiting for the previous view still goes to it.
    sendAutosave()
    sentAutosaved = null
    viewGeneration++
    setView({ baseline, error: null, id: detail.id, status: 'ready', summary: detail })
    return baseline
  }

  /** Make a returned resource the opened baseline without re-applying it to the table. */
  const adoptAsBaseline = (detail: SavedViewDetail) => {
    if (openAsSaved(detail)) adoptColumns(liveColumns)
  }

  const encodeQuery = (query: TQuery) => definition.query.encode?.(query) ?? query

  /** The live query as a save stores it. */
  const savedQuery = () => encodeQuery(liveQuery())

  const liveConfiguration = () => {
    const columns = liveColumns ?? state.view?.baseline?.columns ?? {}
    return { columns: toColumnsJson(columns), query: savedQuery() }
  }

  /** The table's locked columns over code defaults, with the default query. */
  const blankConfiguration = () => {
    if (!blankColumns) throw new Error('The table has not reported its columns')
    return { columns: toColumnsJson(blankColumns), query: encodeQuery(definition.defaultQuery) }
  }

  /** Opens a view just saved as it was saved, discarding what the table showed. */
  const showSaved = (detail: SavedViewDetail) => {
    const baseline = openAsSaved(detail)
    if (!baseline) return
    navigate('push', urlFor(baseline.query, detail.id))
    setColumnSource(baseline.columns)
  }

  /** What a new view stores, and how it opens once saved, by what it starts from. */
  const newViewFrom = (start: SavedViewStart) =>
    start === 'blank'
      ? { configuration: blankConfiguration(), open: showSaved }
      : {
          configuration: liveConfiguration(),
          open: (detail: SavedViewDetail) => {
            adoptAsBaseline(detail)
            // As saved: `encode` may have changed the live query.
            navigate('push', urlFor(savedQuery(), detail.id))
          },
        }

  const requireConnection = () => {
    if (!connection) throw new Error('Table Configuration controller is not connected')
    return connection
  }

  const columnOwner: TableColumnOwner = {
    getSource: () => columnSource,
    report: (reported, origin) => {
      const columns = { ...reported, density }
      liveColumns = columns
      // Applied columns are the source's, density included, even after a density change.
      if (origin === 'applied')
        appliedColumns = { ...reported, density: columnSource.columns?.density }
      if (origin === 'resized') queueAutosave('sizing')
      // Widths changed by anything but a resize, such as a reset, are never saved automatically.
      // A density change still waiting to be saved is queued again without them.
      if (
        origin === 'edited' &&
        pendingAutosave &&
        !equalAutosaved(columns, pendingAutosave.autosaved)
      ) {
        dropPendingAutosave()
        queueAutosave('density')
      }
      emit()
    },
    reportBlank: columns => {
      const wasReported = blankColumns !== null
      blankColumns = columns
      if (wasReported !== (columns !== null)) emit()
    },
    subscribe: listener => {
      columnListeners.add(listener)
      return () => columnListeners.delete(listener)
    },
  }

  return {
    columnOwner,

    connect: (next: TableConfigurationConnection) => {
      const isFirst = connection === null
      connection = next
      if (isFirst) reconcile()
    },

    /** The registered table's name, paths and key, for UI that serves any table. */
    definition: {
      label: definition.label,
      pathname: definition.pathname,
      tableKey: definition.tableKey,
    },

    deleteView: async (id: number) => {
      const { api, queryClient } = requireConnection()
      if (state.view?.id === id) cancelAutosave()
      await api.deleteSavedView.mutate({ path: { ...path, id } })
      queryClient.removeQueries({ queryKey: api.savedView.queryKey({ path: { ...path, id } }) })
      void queryClient.invalidateQueries({ queryKey: savedViewsKey })
      if (state.view?.id === id) returnToDefault('replace')
      void loadPinned(true)
    },

    discardChanges: () => {
      cancelAutosave()
      const { view } = state
      if (view === null) {
        applyDefaultColumns()
        return emit()
      }
      if (!view.baseline) return
      navigate('replace', urlFor(view.baseline.query, view.id))
      setColumnSource(view.baseline.columns)
      emit()
    },

    dismissNotice: (id: number) => {
      state = { ...state, notices: state.notices.filter(notice => notice.id !== id) }
      emit()
    },

    getServerSnapshot: () => serverSnapshot,
    getSnapshot: () => snapshot,

    open: async (id: number) => {
      if (!isActive() || state.view?.id === id) return
      const generation = ++viewGeneration
      state = { ...state, opening: id }
      emit()

      const keepSelection = (notice: TableConfigurationNotice['kind']) => {
        state = { ...state, opening: null }
        addNotice(notice)
        // The open superseded a view still loading from the URL; load it again.
        if (state.view?.status === 'loading') return reconcile(true)
        emit()
      }

      const result = await settle(fetchDetail(id))
      if (generation !== viewGeneration) return
      if (!result.ok) {
        if (isAccessLost(result.error)) return keepSelection('view-unavailable')
        state = { ...state, opening: null }
        emit()
        throw result.error
      }
      const detail = result.value

      if (!detail.available) return keepSelection('wrong-organisation')

      const baseline = toBaseline(detail)
      if (!baseline) {
        // A future schema fails closed: code defaults plus a retryable error.
        const error = new UnsupportedTableConfigurationError(detail.schema_version)
        setView({ baseline: null, error, id, status: 'error', summary: detail })
        navigate('push', urlFor(definition.defaultQuery, id))
        setColumnSource(undefined)
        return emit()
      }

      setView({ baseline, error: null, id, status: 'ready', summary: detail })
      navigate('push', urlFor(baseline.query, id))
      setColumnSource(baseline.columns)
      emit()
    },

    openDefault: () => {
      if (!isActive() || state.view === null) return
      returnToDefault('push')
    },

    pin: async (id: number) => {
      const { api, queryClient } = requireConnection()
      await api.pin.mutate({ path: { ...path, id } })
      // Summaries carry `is_pinned`; mark every cached list stale.
      void queryClient.invalidateQueries({ queryKey: savedViewsKey })
      await refresh()
    },

    /** Recheck the active view for Updated and reload pins. No polling. */
    refresh: () => refresh(),

    reload: () => {
      const { updated, view } = state
      if (!updated || !view) return
      cancelAutosave()
      const baseline = toBaseline(updated)
      if (!baseline) {
        const error = new UnsupportedTableConfigurationError(updated.schema_version)
        setView({ ...view, error, status: 'error' })
        return emit()
      }
      setView({ baseline, error: null, id: view.id, status: 'ready', summary: updated })
      navigate('replace', urlFor(baseline.query, view.id))
      setColumnSource(baseline.columns)
      emit()
    },

    /**
     * Shows `ids` in their new order at once. Pins not listed (hidden as unavailable) keep their
     * places here and on the server. A failure restores the order shown before and rejects.
     */
    reorderPins: async (ids: readonly number[]) => {
      const { api, queryClient } = requireConnection()
      const previous = state.pinned.items
      const version = ++pinOrderVersion
      pinOrderSaves++
      state = { ...state, pinned: { ...state.pinned, items: reorderListed(previous, ids) } }
      emit()

      const save = pinOrderWrites.then(() =>
        settle(api.reorderPins.mutate({ input: { saved_view_ids: [...ids] }, path })),
      )
      pinOrderWrites = save
      const result = await save
      pinOrderSaves--
      // A newer reorder lists every shown pin, so its own save sets the order shown.
      if (!result.ok && version === pinOrderVersion) {
        state = { ...state, pinned: { ...state.pinned, items: previous } }
        emit()
        reloadPinsAfterSaves = true
      }
      if (pinOrderSaves === 0 && reloadPinsAfterSaves) {
        reloadPinsAfterSaves = false
        void loadPinned(true)
      }
      if (!result.ok) throw result.error
      // `access=pinned` lists follow the pin order; mark every cached list stale.
      void queryClient.invalidateQueries({ queryKey: savedViewsKey })
    },

    retryDefault: () => loadDefault(),
    retryPinned: () => loadPinned(true),
    retryView: () => reconcile(true),

    saveAsNew: async (
      metadata: Omit<CreateSavedViewInput, 'configuration' | 'schema_version'>,
      start: SavedViewStart,
    ) => {
      const { api } = requireConnection()
      const { configuration, open } = newViewFrom(start)
      const generation = viewGeneration
      const detail = await api.createSavedView.mutate({
        input: { ...metadata, configuration, schema_version: definition.schemaVersion },
        path,
      })
      acceptDetail(detail)
      // Opening another view meanwhile wins over showing this one.
      if (generation === viewGeneration) open(detail)
      emit()
      void loadPinned(true)
      return detail
    },

    /** Owner save. Intentionally last-write-wins over a newer canonical configuration. */
    saveChanges: async () => {
      const { view } = state
      if (!view) throw new Error('No Saved View is open')
      const { api } = requireConnection()
      // This save carries the live widths and density; it lands after any autosave already sent.
      cancelAutosave()
      const configuration = liveConfiguration()
      const detail = await serializeWrite(() =>
        api.updateSavedView.mutate({
          input: { configuration, schema_version: definition.schemaVersion },
          path: { ...path, id: view.id },
        }),
      )
      acceptDetail(detail)
      if (state.view?.id === detail.id) {
        adoptAsBaseline(detail)
        // Show what was saved when `encode` changed the live query.
        const saved = savedQuery()
        if (!definition.query.equals(saved, liveQuery()))
          navigate('replace', urlFor(saved, detail.id))
      }
      emit()
      return detail
    },

    /** Save the live columns, never the query, as the personal Default. */
    saveColumnsToDefault: async () => {
      const columns = liveColumns
      if (!columns) return
      if (state.view === null) cancelAutosave()
      const { api } = requireConnection()
      const resource = await serializeWrite(() =>
        api.updateDefault.mutate({
          input: {
            configuration: { columns: toColumnsJson(columns), query: {} },
            schema_version: definition.schemaVersion,
          },
          path,
        }),
      )
      acceptDefault(resource)
      // On Default the saved columns become the baseline; an open view keeps its own.
      if (state.view === null) adoptColumns(columns)
      emit()
    },

    /** Shows rows at `next` density; it saves like a resize where the user may save. */
    setDensity: (next: RowDensity) => {
      density = next
      if (liveColumns) liveColumns = { ...liveColumns, density }
      queueAutosave('density')
      emit()
    },

    setGate: (gate: { allowed: boolean; contextKey: string }) => {
      const contextChanged = wasActive && gate.contextKey !== contextKey
      allowed = gate.allowed
      contextKey = gate.contextKey
      // Organisation context decides availability, so revalidate the active view.
      reconcile(contextChanged)
    },

    setLocation: (pathname: string, search: string) => {
      if (location?.pathname === pathname && location.search === search) return
      location = { pathname, search }
      reconcile()
    },

    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },

    transfer: async (id: number, newOwnerUserId: number) => {
      const detail = await requireConnection().api.transferSavedView.mutate({
        input: { new_owner_user_id: newOwnerUserId },
        path: { ...path, id },
      })
      acceptDetail(detail)
      emit()
      return detail
    },

    unpin: async (id: number) => {
      const { api, queryClient } = requireConnection()
      await api.unpin.mutate({ path: { ...path, id } })
      // Summaries carry `is_pinned`; mark every cached list stale.
      void queryClient.invalidateQueries({ queryKey: savedViewsKey })
      await refresh()
    },

    updateAudience: async (id: number, input: UpdateSavedViewAudienceInput) => {
      const detail = await requireConnection().api.updateAudience.mutate({
        input,
        path: { ...path, id },
      })
      acceptDetail(detail)
      emit()
      return detail
    },

    updateDetails: async (id: number, input: SavedViewMetadataInput) => {
      const detail = await requireConnection().api.updateSavedView.mutate({
        input,
        path: { ...path, id },
      })
      acceptDetail(detail)
      emit()
      return detail
    },

    /** Goes to the list (`null`) or a view from any route; `open` changes the table in place. */
    visit: (id: null | number) => {
      requireConnection().navigate.push(pathFor(id))
    },
  }

  /** Pins refresh on any route, for the sidebar; the open view only on the table's own. */
  async function refresh() {
    if (!allowed || !connection) return
    await Promise.all([loadPinned(true), isActive() ? recheckView() : undefined])
  }

  async function recheckView() {
    const { view } = state
    if (view?.status !== 'ready' || !view.baseline) return

    const generation = viewGeneration
    const result = await settle(fetchDetail(view.id, true))
    const current = state.view
    if (generation !== viewGeneration || !current?.baseline) return
    if (!result.ok) {
      // Other failures keep the opened view; the next recheck retries.
      if (isAccessLost(result.error)) returnToDefault('replace', 'view-unavailable')
      return
    }
    const latest = result.value
    if (!latest.available) return returnToDefault('replace', 'wrong-organisation')

    const isNewer =
      Date.parse(latest.configuration_updated_at) >
      Date.parse(current.baseline.configurationUpdatedAt)
    state = {
      ...state,
      updated: isNewer ? latest : null,
      view: { ...current, summary: latest },
    }
    emit()
  }
}

export type TableConfigurationController<TQuery extends TableConfigurationJson> = ReturnType<
  typeof createTableConfigurationController<TQuery>
>
